import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const LS_KEY = "kreuse_lang";

const FR = {
  langToggle: { fr: "FR", en: "EN", switchTo: "Passer en anglais", current: "Français" },
  nav: {
    Accueil: "Accueil",
    Services: "Services",
    Urgence: "Urgence",
    Location: "Location",
    Conseils: "Conseils",
    Avis: "Avis",
    Contact: "Contact",
  },
  header: { devis: "Devis gratuit", menu: "Menu" },
  hero: {
    zone: "Intervention en Creuse et alentours",
    title1: "Votre extérieur,",
    title2: "notre passion",
    subtitle:
      "Création et entretien de parcs et jardins, élagage, traitement contre les nuisibles. K'REUSE EXTÉRIEUR prend soin de votre extérieur en Creuse, avec rigueur et passion.",
    ctaPrimary: "Demander un devis gratuit",
    ctaSecondary: "Découvrir nos services",
    badge1Title: "Paysagiste",
    badge1Sub: "Parcs & jardins",
    badge2Title: "Anti-nuisibles",
    badge2Sub: "Traitement professionnel",
    floatBadge: "Devis gratuit",
    floatBadgeSub: "Réponse sous 24h, intervention rapide",
    satisfaction: "Satisfaction client",
  },
  about: {
    eyebrow: "À propos",
    title1: "Un savoir-faire au service de",
    title2: "vos extérieurs",
    p1:
      "K'REUSE EXTÉRIEUR est une entreprise locale spécialisée dans l'aménagement et l'entretien de parcs et jardins, ainsi que dans la lutte contre les nuisibles. Implantée au cœur de la Creuse, nous intervenons chez les particuliers, professionnels et collectivités.",
    p2:
      "Notre approche : du conseil sur-mesure, un travail soigné et le respect du vivant. Que ce soit pour créer un jardin de rêve, entretenir vos espaces verts ou éliminer un nid de frelons, nous mettons notre expertise à votre service.",
    values: [
      { title: "Passion du vivant", desc: "Chaque jardin est unique. Nous mettons notre savoir-faire au service de votre projet." },
      { title: "Travail soigné", desc: "Du conseil à la réalisation, exigence et qualité à chaque étape." },
      { title: "Réactivité", desc: "Devis rapide, interventions ponctuelles ou contrat d'entretien régulier." },
      { title: "Proximité", desc: "Une entreprise locale, à votre écoute, en Creuse et alentours." },
    ],
  },
  services: {
    eyebrow: "Nos services",
    title1: "Deux expertises,",
    title2: "une seule équipe à votre service",
    creditTitle: "Crédit d'impôt 50%",
    creditSub: "Services à la personne — Entretien jardin éligible",
    paysagisteEyebrow: "Paysagiste",
    paysagisteTitle: "Parcs et jardins",
    certiTitle: "Entreprise agréée Certibiocide",
    certiSub: "Certification d'État · Traitements biocides raisonnés",
    nuisiblesEyebrow: "Traitement professionnel",
    nuisiblesTitle: "Lutte anti-nuisibles",
    paysagiste: [
      { title: "Création de jardins", desc: "Conception sur-mesure d'espaces verts adaptés à votre terrain et vos envies." },
      { title: "Entretien de jardins", desc: "Contrats d'entretien réguliers : tonte, désherbage, nettoyage des massifs." },
      { title: "Élagage & abattage", desc: "Taille et abattage d'arbres en toute sécurité, évacuation des déchets verts." },
      { title: "Tonte de pelouse", desc: "Tonte régulière ou ponctuelle pour une pelouse impeccable toute l'année." },
      { title: "Taille de haies", desc: "Taille précise et soignée de vos haies pour un extérieur structuré." },
      { title: "Débroussaillage", desc: "Nettoyage des terrains en friche, mise en conformité débroussaillement." },
    ],
    nuisibles: [
      { title: "Frelons asiatiques", desc: "Destruction de nids en hauteur avec équipement spécialisé." },
      { title: "Guêpes & frelons", desc: "Destruction de nids de guêpes et frelons (y compris asiatiques) en sécurité." },
      { title: "Punaises de lit", desc: "Traitement à la vapeur sèche 180 °C ou biocide homologué, protocole en 3 passages, garantie de résultat." },
      { title: "Cafards & blattes", desc: "Traitement ciblé des locaux infestés, gel appâts et pulvérisation professionnelle." },
      { title: "Vrillettes du bois", desc: "Traitement curatif des charpentes, poutres et meubles anciens contre insectes xylophages." },
      { title: "Chenilles processionnaires", desc: "Échenillage et pose d'éco-pièges contre les chenilles du pin et du chêne." },
      { title: "Fouines & nuisibles", desc: "Solutions adaptées contre les fouines, blaireaux et autres nuisibles." },
      { title: "Rats & souris", desc: "Diagnostic, piégeage et traitement durable contre les rongeurs." },
    ],
  },
  urgence: {
    badge: "Urgence nuisibles",
    title1: "Un nid, une invasion ?",
    title2: "On intervient vite.",
    callEyebrow: "Appel direct — 7j/7",
    callTap: "Appuyez pour appeler",
    waTitle: "WhatsApp",
    waSub: "Envoyez une photo, devis express",
    treatedLabel: "On traite :",
    treatedItems: ["Frelons asiatiques", "Guêpes", "Rongeurs", "Chenilles processionnaires", "Cafards", "Punaises de lit", "Vrillettes du bois"],
    trustRapide: "Intervention rapide",
    trustRapideSub: "Réponse en journée",
    trustPro: "Matériel pro",
    trustProSub: "Équipement sécurisé",
    seasonal: "Saison à risque",
    seasonalSub: "Avril → Octobre (frelons, guêpes)",
    waMessage: "URGENCE — J'ai besoin d'une intervention rapide (lutte anti-nuisibles).",
  },
  location: {
    eyebrow: "Location de matériel",
    title1: "Du matériel pro,",
    title2: "à la journée ou plus",
    intro: "Besoin ponctuel d'un outil professionnel ? Louez chez nous, en Creuse. Tarifs clairs, caution restituée au retour.",
    deposit: "Caution",
    reserve: "Réserver",
    bottomTitle: "Une question sur la disponibilité ?",
    bottomSub: "Appelez-nous ou envoyez votre demande directement par WhatsApp. Nous vous répondons rapidement avec les créneaux libres.",
    cgv: "Consulter les conditions générales de location (PDF)",
    waMessage: "Bonjour, je souhaite louer du matériel chez K'REUSE EXTÉRIEUR.",
    photoSoon: "Photo bientôt",
    durations: { day: "1 jour", weekend: "Week-end", week: "Semaine", month: "Mois" },
    depositMethods: { "Chèque ou espèces": "Chèque ou espèces", "Chèque": "Chèque" },
    dialog: {
      title: "Réserver",
      subtitle: "Pré-réservation — nous vous confirmons par téléphone ou email",
      duration: "Durée",
      startDate: "Date de début souhaitée",
      pickDate: "Choisir une date",
      name: "Nom complet",
      namePh: "Jean Dupont",
      email: "Email",
      phone: "Téléphone",
      phonePh: "06 12 34 56 78",
      emailPh: "email@exemple.com",
      message: "Message (optionnel)",
      messagePh: "Précisions, lieu de retrait, questions…",
      depositLine: "Caution",
      submit: "Envoyer ma demande",
      sending: "Envoi en cours...",
      successTitle: "Demande envoyée !",
      successText: "Nous revenons vers vous très rapidement pour confirmer la disponibilité et finaliser votre réservation.",
      successClose: "Fermer",
      errorMissing: "Merci de remplir les champs obligatoires.",
      toastOk: "Demande envoyée !",
      errorGeneric: "Erreur lors de l'envoi. Réessayez.",
    },
  },
  serviceArea: {
    eyebrow: "Zone d'intervention",
    title1: "Nous intervenons en",
    title2: "Creuse",
    title3: "et alentours",
    intro: "Basés au cœur de la Creuse (département 23), nous nous déplaçons dans toute la région pour vos besoins en paysagisme et lutte contre les nuisibles.",
    phoneLabel: "Téléphone",
    emailLabel: "Email",
    deptLabel: "Département Creuse",
  },
  faq: {
    eyebrow: "Questions fréquentes",
    title1: "Tout ce que",
    title2: "vous voulez savoir",
    items: [
      { q: "Intervenez-vous dans toute la Creuse ?", a: "Oui, K'REUSE EXTÉRIEUR intervient dans tout le département de la Creuse (23) : Guéret, La Souterraine, Aubusson, Bourganeuf, Boussac, La Celle-Dunoise et villages alentours." },
      { q: "Quel est le délai pour un devis ?", a: "Réponse sous 24h en moyenne. Pour les urgences (frelons, guêpes), nous traitons en priorité dans la journée." },
      { q: "L'entretien de jardin est-il éligible au crédit d'impôt 50% ?", a: "Oui. Les prestations de petits travaux de jardinage (tonte, taille de haies, débroussaillage) sont éligibles au crédit d'impôt de 50%, dans la limite de 5 000 €/an par foyer fiscal (services à la personne)." },
      { q: "Êtes-vous agréé pour les traitements anti-nuisibles ?", a: "Oui, K'REUSE EXTÉRIEUR est une entreprise certifiée Certibiocide (agrément d'État) pour l'utilisation de produits biocides et l'application de traitements raisonnés." },
      { q: "Comment se déroule la destruction d'un nid de frelons ?", a: "Après diagnostic (sur place ou via photo WhatsApp), nous intervenons avec une combinaison intégrale et un équipement spécialisé. Le périmètre est sécurisé, le nid est neutralisé, et nous assurons un suivi 24h pour vérifier le résultat." },
    ],
  },
  contact: {
    eyebrow: "Contact",
    title1: "Parlons de",
    title2: "votre projet",
    intro: "Un projet d'aménagement, un nid de frelons à traiter, un entretien régulier ? Décrivez-nous votre besoin, nous vous répondons sous 24h avec un devis gratuit.",
    phoneLabel: "Téléphone",
    emailLabel: "Email",
    zoneLabel: "Zone d'intervention",
    zoneValue: "Creuse (23) et alentours",
    nameLabel: "Nom complet",
    namePh: "Jean Dupont",
    emailFieldLabel: "Email",
    emailPh: "jean@exemple.fr",
    phoneFieldLabel: "Téléphone",
    phonePh: "06 12 34 56 78",
    serviceLabel: "Service souhaité",
    servicePh: "Sélectionner...",
    services: [
      "Création de jardin",
      "Entretien régulier",
      "Élagage / abattage",
      "Tonte de pelouse",
      "Taille de haies",
      "Débroussaillage",
      "Destruction nid de guêpes/frelons",
      "Traitement rongeurs",
      "Chenilles processionnaires",
      "Cafards / blattes",
      "Autre demande",
    ],
    messageLabel: "Votre message",
    messagePh: "Décrivez votre projet ou votre besoin...",
    submit: "Envoyer ma demande",
    submitting: "Envoi en cours...",
    rgpd: "Vos données sont utilisées uniquement pour répondre à votre demande. Aucun spam.",
    or: "ou",
    waCta: "Contacter sur WhatsApp",
    waMessage: "Bonjour, je souhaite obtenir un devis pour K'REUSE EXTÉRIEUR.",
    successTitle: "Merci pour votre demande !",
    successText: "Nous revenons vers vous dans les plus brefs délais, sous 24h en moyenne.",
    newRequest: "Nouvelle demande",
    errorMissing: "Merci de remplir les champs obligatoires.",
    toastOk: "Votre demande a bien été envoyée !",
    errorGeneric: "Erreur lors de l'envoi. Réessayez.",
  },
  footer: {
    tagline: "Parcs & jardins · Anti-nuisibles",
    intro: "Votre partenaire local en Creuse pour la création et l'entretien de vos espaces extérieurs, ainsi que la lutte contre les nuisibles.",
    facebook: "Suivez-nous sur Facebook",
    navTitle: "Navigation",
    servicesTitle: "Nos services",
    citiesTitle: "Villes desservies",
    blogTitle: "Conseils & actualités",
    blogAll: "Tous nos articles →",
    contactTitle: "Contact",
    zone: "Creuse (23) et alentours",
    mentions: "Mentions légales",
    rgpd: "Politique de confidentialité",
    cgv: "CGV de location",
    rights: "Tous droits réservés.",
    made: "Site réalisé avec passion 🌿",
    serviceLinks: [
      { label: "Paysagiste en Creuse", href: "/paysagiste-creuse" },
      { label: "Entretien jardin (crédit d'impôt 50%)", href: "/entretien-jardin-creuse" },
      { label: "Élagage / abattage", href: "/elagage-creuse" },
      { label: "Destruction nid de frelons", href: "/destruction-frelons-creuse" },
      { label: "Dératisation (rats, souris)", href: "/deratisation-creuse" },
      { label: "Cafards et blattes", href: "/cafards-blattes-creuse" },
      { label: "Chenilles processionnaires", href: "/chenilles-processionnaires-creuse" },
    ],
  },
};

const EN = {
  langToggle: { fr: "FR", en: "EN", switchTo: "Switch to French", current: "English" },
  nav: {
    Accueil: "Home",
    Services: "Services",
    Urgence: "Emergency",
    Location: "Rentals",
    Conseils: "Advice",
    Avis: "Reviews",
    Contact: "Contact",
  },
  header: { devis: "Free quote", menu: "Menu" },
  hero: {
    zone: "Serving Creuse (France) and surroundings",
    title1: "Your outdoors,",
    title2: "our passion",
    subtitle:
      "Design and maintenance of parks and gardens, tree pruning, pest control. K'REUSE EXTÉRIEUR takes care of your outdoors in the Creuse region, with skill and dedication.",
    ctaPrimary: "Get a free quote",
    ctaSecondary: "Discover our services",
    badge1Title: "Landscaping",
    badge1Sub: "Parks & gardens",
    badge2Title: "Pest control",
    badge2Sub: "Professional treatment",
    floatBadge: "Free quote",
    floatBadgeSub: "Reply within 24h, quick intervention",
    satisfaction: "Customer satisfaction",
  },
  about: {
    eyebrow: "About us",
    title1: "Expert care for",
    title2: "your outdoor spaces",
    p1:
      "K'REUSE EXTÉRIEUR is a local company specialised in the design and upkeep of parks and gardens, as well as professional pest control. Based in the heart of the Creuse, we work for homeowners, businesses and local authorities.",
    p2:
      "Our approach: tailored advice, careful workmanship and respect for nature. From designing your dream garden to maintaining your green spaces or removing a hornet's nest, we bring our expertise to your project.",
    values: [
      { title: "Passion for nature", desc: "Every garden is unique. We put our craft at the service of your project." },
      { title: "Careful workmanship", desc: "From advice to delivery, quality and attention at every step." },
      { title: "Responsiveness", desc: "Fast quotes, one-off jobs or regular maintenance contracts." },
      { title: "Local & close", desc: "A local company, available and on-the-ground in Creuse." },
    ],
  },
  services: {
    eyebrow: "Our services",
    title1: "Two areas of expertise,",
    title2: "one dedicated team",
    creditTitle: "50% tax credit (FR residents)",
    creditSub: "French 'services à la personne' — garden upkeep eligible",
    paysagisteEyebrow: "Landscaping",
    paysagisteTitle: "Parks & gardens",
    certiTitle: "Certibiocide-approved company",
    certiSub: "French state certification · Responsible biocide treatments",
    nuisiblesEyebrow: "Professional treatment",
    nuisiblesTitle: "Pest control",
    paysagiste: [
      { title: "Garden design", desc: "Bespoke design of green spaces tailored to your land and tastes." },
      { title: "Garden maintenance", desc: "Regular contracts: mowing, weeding, flowerbed cleaning." },
      { title: "Pruning & felling", desc: "Safe pruning and tree felling, green-waste removal included." },
      { title: "Lawn mowing", desc: "Regular or one-off mowing for a flawless lawn all year round." },
      { title: "Hedge trimming", desc: "Precise, clean hedge trimming for a structured outdoor look." },
      { title: "Brush clearing", desc: "Clearing overgrown plots, compliance with French clearing regulations." },
    ],
    nuisibles: [
      { title: "Asian hornets", desc: "Removal of high nests with specialised equipment." },
      { title: "Wasps & hornets", desc: "Safe destruction of wasp and hornet nests (including Asian hornets)." },
      { title: "Bed bugs", desc: "180 °C dry-steam treatment or approved biocide, 3-pass protocol, guaranteed result." },
      { title: "Cockroaches", desc: "Targeted treatment of infested premises, gel baits and professional spraying." },
      { title: "Woodworms", desc: "Curative treatment of framework, beams and antique furniture against wood-boring insects." },
      { title: "Processionary caterpillars", desc: "Removal and eco-trap setup against pine and oak caterpillars." },
      { title: "Stone martens & wildlife", desc: "Tailored solutions for stone martens, badgers and other nuisance wildlife." },
      { title: "Rats & mice", desc: "Diagnosis, trapping and long-term rodent control." },
    ],
  },
  urgence: {
    badge: "Pest emergency",
    title1: "A nest, an invasion?",
    title2: "We act fast.",
    callEyebrow: "Direct call — 7 days a week",
    callTap: "Tap to call",
    waTitle: "WhatsApp",
    waSub: "Send a photo, fast quote",
    treatedLabel: "We handle:",
    treatedItems: ["Asian hornets", "Wasps", "Rodents", "Processionary caterpillars", "Cockroaches", "Bed bugs", "Woodworms"],
    trustRapide: "Fast intervention",
    trustRapideSub: "Same-day reply",
    trustPro: "Professional gear",
    trustProSub: "Certified equipment",
    seasonal: "Peak season",
    seasonalSub: "April → October (hornets, wasps)",
    waMessage: "EMERGENCY — I need a fast pest-control intervention.",
  },
  location: {
    eyebrow: "Equipment rental",
    title1: "Pro-grade equipment,",
    title2: "by the day or longer",
    intro: "Need a professional tool for a one-off job? Rent from us in Creuse — transparent rates, deposit returned on hand-back.",
    deposit: "Deposit",
    reserve: "Book it",
    bottomTitle: "Question about availability?",
    bottomSub: "Call us or send a WhatsApp message. We'll get back to you quickly with the open slots.",
    cgv: "View rental terms & conditions (PDF, FR)",
    waMessage: "Hi, I'd like to rent some equipment at K'REUSE EXTÉRIEUR.",
    photoSoon: "Photo coming soon",
    durations: { day: "1 day", weekend: "Weekend", week: "Week", month: "Month" },
    depositMethods: { "Chèque ou espèces": "Cheque or cash", "Chèque": "Cheque" },
    dialog: {
      title: "Book",
      subtitle: "Pre-booking — we'll confirm by phone or email",
      duration: "Duration",
      startDate: "Preferred start date",
      pickDate: "Pick a date",
      name: "Full name",
      namePh: "John Doe",
      email: "Email",
      phone: "Phone",
      phonePh: "+33 6 12 34 56 78",
      emailPh: "email@example.com",
      message: "Message (optional)",
      messagePh: "Details, pickup location, questions…",
      depositLine: "Deposit",
      submit: "Send my request",
      sending: "Sending...",
      successTitle: "Request sent!",
      successText: "We'll get back to you very soon to confirm availability and finalise your booking.",
      successClose: "Close",
      errorMissing: "Please fill in the required fields.",
      toastOk: "Request sent!",
      errorGeneric: "Something went wrong. Please try again.",
    },
  },
  serviceArea: {
    eyebrow: "Service area",
    title1: "We operate across the",
    title2: "Creuse",
    title3: "and surroundings",
    intro: "Based at the heart of the Creuse (department 23, France), we travel across the whole region for landscaping and pest-control needs.",
    phoneLabel: "Phone",
    emailLabel: "Email",
    deptLabel: "Creuse department",
  },
  faq: {
    eyebrow: "Frequently asked questions",
    title1: "Everything",
    title2: "you want to know",
    items: [
      { q: "Do you operate across the whole Creuse?", a: "Yes — K'REUSE EXTÉRIEUR works throughout the Creuse department (23): Guéret, La Souterraine, Aubusson, Bourganeuf, Boussac, La Celle-Dunoise and surrounding villages." },
      { q: "How long does it take to get a quote?", a: "Reply within 24h on average. For emergencies (hornets, wasps), we prioritise same-day handling." },
      { q: "Is garden upkeep eligible for the 50% French tax credit?", a: "Yes. Small garden-maintenance services (mowing, hedge trimming, brush clearing) are eligible for the 50% tax credit, up to €5,000/year per household ('services à la personne')." },
      { q: "Are you certified for pest-control treatments?", a: "Yes — K'REUSE EXTÉRIEUR is Certibiocide-certified (French state approval) to use biocides and apply responsible treatments." },
      { q: "How is a hornet's nest removal carried out?", a: "After diagnosis (on site or via WhatsApp photo), we intervene with a full protective suit and specialised gear. The area is secured, the nest is neutralised, and we follow up 24h later to verify the result." },
    ],
  },
  contact: {
    eyebrow: "Contact",
    title1: "Let's talk about",
    title2: "your project",
    intro: "Landscaping project, hornet nest to remove, regular maintenance? Tell us what you need — we'll reply within 24h with a free quote.",
    phoneLabel: "Phone",
    emailLabel: "Email",
    zoneLabel: "Service area",
    zoneValue: "Creuse (23, France) and surroundings",
    nameLabel: "Full name",
    namePh: "John Doe",
    emailFieldLabel: "Email",
    emailPh: "john@example.com",
    phoneFieldLabel: "Phone",
    phonePh: "+33 6 12 34 56 78",
    serviceLabel: "Desired service",
    servicePh: "Select...",
    services: [
      "Garden design",
      "Regular maintenance",
      "Pruning / felling",
      "Lawn mowing",
      "Hedge trimming",
      "Brush clearing",
      "Wasp / hornet nest removal",
      "Rodent treatment",
      "Processionary caterpillars",
      "Cockroaches",
      "Other request",
    ],
    messageLabel: "Your message",
    messagePh: "Describe your project or need...",
    submit: "Send my request",
    submitting: "Sending...",
    rgpd: "Your data is only used to reply to your request. No spam.",
    or: "or",
    waCta: "Contact us on WhatsApp",
    waMessage: "Hi, I'd like to get a quote from K'REUSE EXTÉRIEUR.",
    successTitle: "Thanks for your request!",
    successText: "We'll get back to you as soon as possible, within 24h on average.",
    newRequest: "New request",
    errorMissing: "Please fill in the required fields.",
    toastOk: "Your request has been sent!",
    errorGeneric: "Something went wrong. Please try again.",
  },
  footer: {
    tagline: "Parks & gardens · Pest control",
    intro: "Your local partner in Creuse for the design and maintenance of your outdoor spaces, and professional pest control.",
    facebook: "Follow us on Facebook",
    navTitle: "Navigation",
    servicesTitle: "Our services",
    citiesTitle: "Cities we serve",
    blogTitle: "Advice & news",
    blogAll: "All articles →",
    contactTitle: "Contact",
    zone: "Creuse (23, France) and surroundings",
    mentions: "Legal notice",
    rgpd: "Privacy policy",
    cgv: "Rental T&Cs",
    rights: "All rights reserved.",
    made: "Built with care 🌿",
    serviceLinks: [
      { label: "Landscaping in Creuse", href: "/paysagiste-creuse" },
      { label: "Garden upkeep (50% tax credit)", href: "/entretien-jardin-creuse" },
      { label: "Pruning / felling", href: "/elagage-creuse" },
      { label: "Hornet nest removal", href: "/destruction-frelons-creuse" },
      { label: "Rat & mouse control", href: "/deratisation-creuse" },
      { label: "Cockroach treatment", href: "/cafards-blattes-creuse" },
      { label: "Processionary caterpillars", href: "/chenilles-processionnaires-creuse" },
    ],
  },
};

const DICTS = { fr: FR, en: EN };

const LanguageContext = createContext({
  lang: "fr",
  setLang: () => {},
  t: (k) => k,
});

function resolve(obj, path) {
  return path.split(".").reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "fr";
    // 1. URL param ?lang= — explicit share link, respect it
    try {
      const param = new URLSearchParams(window.location.search).get("lang");
      if (param === "en" || param === "fr") return param;
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.warn("i18n: failed to read URL lang param", err);
      }
    }
    // 2. localStorage — user has previously chosen a language on this device
    try {
      const stored = window.localStorage.getItem(LS_KEY);
      if (stored === "en" || stored === "fr") return stored;
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.warn("i18n: failed to read localStorage lang", err);
      }
    }
    // 3. Default: French (the business is French-speaking, Creuse).
    //    No navigator.language auto-detect — Google would index EN, polluting SEO.
    return "fr";
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(LS_KEY, lang);
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.warn("i18n: failed to persist lang to localStorage", err);
      }
    }
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("lang", lang);
    }
    // Only reflect the language in the URL when the user EXPLICITLY switched to
    // a non-default language. We avoid writing ?lang=fr (default) so Googlebot
    // sees clean canonical URLs and indexes the French version as authoritative.
    try {
      const url = new URL(window.location.href);
      const current = url.searchParams.get("lang");
      if (lang === "en" && current !== "en") {
        url.searchParams.set("lang", "en");
        window.history.replaceState({}, "", url.toString());
      } else if (lang === "fr" && current) {
        url.searchParams.delete("lang");
        window.history.replaceState({}, "", url.toString());
      }
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.warn("i18n: failed to sync lang to URL", err);
      }
    }
  }, [lang]);

  const setLang = useCallback((l) => setLangState(l === "en" ? "en" : "fr"), [setLangState]);

  const t = useCallback(
    (path) => {
      const dict = DICTS[lang] || DICTS.fr;
      const value = resolve(dict, path);
      if (value === undefined) return resolve(DICTS.fr, path) ?? path;
      return value;
    },
    [lang],
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useT() {
  return useContext(LanguageContext);
}
